const registeredSections = new Set();
if (!customElements.get('mini-cart')) {
  customElements.define('mini-cart', class MiniCart extends HTMLElement {

    connectedCallback() {
      this.patchAccountTriggerLinks();

      this.addEventListener('click', e => {
        const link = e.target.closest('a');
        if (!link || !link.closest('[data-account-trigger]')) return;
        e.preventDefault();
        const accountId = document.querySelector('[data-modal-content^="account-drawer-"]')?.dataset.modalContent;
        if (accountId) {
          window.dispatchEvent(new CustomEvent('modal:openById', { detail: accountId }));
          setTimeout(() => document.querySelector('[data-account-target="register"]')?.click(), 0);
        }
      });
      
      this.sectionId = this.dataset.sectionId;
      window.minicartSectionId = this.sectionId;

      if (this.dataset.isCartPage !== 'true' && !registeredSections.has(this.sectionId)) {
        registeredSections.add(this.sectionId);
        window.addEventListener('shopify:section:select', e => {
          if (e.detail.sectionId !== this.sectionId) return;
          window.dispatchEvent(new CustomEvent('modal:openById', { detail: 'minicart' }));
        });
        window.addEventListener('shopify:section:deselect', e => {
          if (e.detail.sectionId !== this.sectionId) return;
          window.dispatchEvent(new CustomEvent('modal:closeById', { detail: 'minicart' }));
        });
      }

      window.addEventListener('cart:add:pending', () => this.setLoading(true));

      window.addEventListener('productForm:add', e => {
        this.setLoading(true);
        const count = e.detail?.item_count;
        if (count !== undefined) this.updateCartCount(count);
        const sectionHtml = e.detail?.sections?.[this.sectionId];
        (sectionHtml ? Promise.resolve(sectionHtml) : this.fetchSectionHtml())
          .then(html => this.refreshFromHtml(html))
          .finally(() => this.setLoading(false));
      });

      this.addEventListener('click', e => {
        const btn = e.target.closest('[data-cart-action="updateQty"]');
        if (!btn) return;
        const { lineItemId, quantity } = btn.dataset;
        this.changeQty(lineItemId, Number(quantity));
      });

      this.addEventListener('click', e => {
        const btn = e.target.closest('[data-cart-action="removeBundle"]');
        if (!btn) return;
        this.removeBundle(btn.dataset.bundleId, btn.dataset.errorMessage);
      });

      let swipeDragged = false;
      window.addEventListener('pointerdown', () => { swipeDragged = false; }, { passive: true });
      window.addEventListener('pointermove', e => {
        if (!swipeDragged && e.target.closest('swiper-container, swiper-slide, swiper-wrapper')) {
          swipeDragged = true;
        }
      }, { passive: true });

      this.addEventListener('click', e => {
        if (swipeDragged) { swipeDragged = false; return; }
        const link = e.target.closest('a[href]');
        if (!link) return;
        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || link.target === '_blank') return;
        window.dispatchEvent(new CustomEvent('modal:closeById', { detail: 'minicart' }));
      });
    }

    async changeQty(lineItemId, quantity) {
      this.setLoading(true);
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItemId, quantity, sections: [this.sectionId] }),
        });
        const res = await r.json();

        if (!r.ok) {
          this.showError(res.description || 'Unable to update quantity.');
          return;
        }

        this.updateCartCount(res.item_count);
        window.dispatchEvent(new CustomEvent('cart:update', { detail: res }));

        const html = res.sections?.[this.sectionId] ?? await this.fetchSectionHtml();
        this.refreshFromHtml(html);
      } catch (err) {
        console.error('MiniCart: qty update failed', err);
      } finally {
        this.setLoading(false);
      }
    }

    // Removes every line sharing a _bundle_id so the bundle discount never applies to a partial set
    async removeBundle(bundleId, errorMessage) {
      if (!bundleId) return;
      this.setLoading(true);
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const updates = {};
        cart.items.forEach(item => {
          if (item.properties?._bundle_id === bundleId) updates[item.key] = 0;
        });
        if (!Object.keys(updates).length) return;

        const r = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates, sections: [this.sectionId] }),
        });
        const res = await r.json();

        if (!r.ok) {
          this.showError(res.description || errorMessage);
          return;
        }

        this.updateCartCount(res.item_count);
        window.dispatchEvent(new CustomEvent('cart:update', { detail: res }));

        const html = res.sections?.[this.sectionId] ?? await this.fetchSectionHtml();
        this.refreshFromHtml(html);
      } catch (err) {
        console.error('MiniCart: bundle remove failed', err);
        this.showError(errorMessage);
      } finally {
        this.setLoading(false);
      }
    }

    showError(msg) {
      const el = this.querySelector('[data-cart-error]');
      if (!el) return;
      el.textContent = msg;
      el.classList.remove('max-h-0', 'opacity-0', 'py-0', 'mb-0');
      el.classList.add('max-h-20', 'opacity-100', 'py-3', 'mb-4');
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(() => {
        el.classList.remove('max-h-20', 'opacity-100', 'py-3', 'mb-4');
        el.classList.add('max-h-0', 'opacity-0', 'py-0', 'mb-0');
      }, 4000);
    }

    async fetchSectionHtml() {
      const data = await fetch(`/?sections=${this.sectionId}`).then(r => r.json());
      return data[this.sectionId];
    }

    refreshFromHtml(html) {
      if (!html) return;
      const wrap = document.createElement('div');
      wrap.innerHTML = html;

      const fresh = {};
      wrap.querySelectorAll('[data-section]').forEach(el => {
        fresh[el.dataset.section] = el.innerHTML;
      });
      wrap.querySelectorAll('template').forEach(t => {
        t.content.querySelectorAll('[data-section]').forEach(el => {
          fresh[el.dataset.section] = el.innerHTML;
        });
      });

      // Skip upsells to preserve dynamically-appended products
      this.querySelectorAll('[data-section]').forEach(section => {
        if (section.dataset.section === 'cart-upsell-items') return;
        const newHtml = fresh[section.dataset.section];
        if (newHtml !== undefined) section.innerHTML = newHtml;
      });

      // Keep source template in sync so re-opens don't show stale server-rendered state
      const sourceTemplate = document.querySelector('template[data-modal-content="minicart"]');
      if (sourceTemplate) {
        sourceTemplate.content.querySelectorAll('[data-section]').forEach(section => {
          if (section.dataset.section === 'cart-upsell-items') return;
          const newHtml = fresh[section.dataset.section];
          if (newHtml !== undefined) section.innerHTML = newHtml;
        });
      }

      this.patchAccountTriggerLinks();
    }

    patchAccountTriggerLinks() {
      this.querySelectorAll('[data-account-trigger] a').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('href', '#');
      });
    }

    setLoading(on) {
      const wrapper = this.closest('[data-cart-page-portal]')
        ?? this.closest('[data-cart-loading-wrapper]')
        ?? this;
      wrapper.classList.toggle('loading', on);
    }

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count > 0 ? count : '';
      });
      document.querySelectorAll('[data-cart-item-label]').forEach(el => {
        el.textContent = count > 0 ? `${count} item${count === 1 ? '' : 's'}` : '';
      });
      const announcer = document.querySelector('[data-cart-announcer]');
      if (announcer) {
        announcer.textContent = '';
        requestAnimationFrame(() => {
          announcer.textContent = count > 0
            ? `Cart updated. ${count} item${count !== 1 ? 's' : ''}.`
            : 'Your cart is empty.';
        });
      }
    }
  });
}

if (!customElements.get('cart-purchase-progress')) {
  customElements.define('cart-purchase-progress', class CartPurchaseProgress extends HTMLElement {

    connectedCallback() {
      this.threshold   = Number(this.dataset.threshold);
      this.copy        = this.dataset.copy        || '';
      this.successCopy = this.dataset.successCopyLoggedin || this.dataset.successCopy || '';
      this.copyEl      = this.closest('[data-progress-wrapper]')?.querySelector('[data-progress-copy]');

      if (!this.threshold && !this.dataset.successCopyLoggedin) {
        this.closest('[data-progress-wrapper]')?.remove();
        return;
      }

      this.fetchAndUpdate();

      window.addEventListener('cart:update', e => {
        this.updateSelf(e.detail.total_price, e.detail.item_count);
      });

      window.addEventListener('productForm:add', () => {
        this.fetchAndUpdate();
      });
    }

    fetchAndUpdate() {
      fetch('/cart.json')
        .then(r => r.json())
        .then(cart => this.updateSelf(cart.total_price, cart.item_count));
    }

    updateSelf(totalPrice, itemCount) {
      if (totalPrice == null || isNaN(totalPrice)) return;
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = itemCount > 0 ? itemCount : '';
      });
      document.querySelectorAll('[data-cart-item-label]').forEach(el => {
        el.textContent = itemCount > 0 ? `${itemCount} item${itemCount === 1 ? '' : 's'}` : '';
      });

      const pct = Math.min((totalPrice / this.threshold) * 100, 100);
      this.style.setProperty('--progress-percentage', `${pct}%`);

      if (this.copyEl) {
        if (totalPrice >= this.threshold) {
          this.copyEl.textContent = this.successCopy;
        } else {
          const diff = ((this.threshold - totalPrice) / 100).toFixed(2);
          this.copyEl.textContent = this.copy.replace('_difference_', `$${diff}`);
        }
      }
    }
  });
}
